import axios from "axios";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import CustomSnackbar from "../_src_shared/components/CustomSnackbar";
import { loanFetchedDataLoanTermsSet } from "../redux/reducers/loanFetchedDataReducer";
import { useAxiosHook } from "./useAxiosHook";

const ROUTE_PREFIX = "loan_terms";
export const useLoanTermsHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // Fee/escrow list-object writes return **409** when the item changed under us
  // (a concurrent edit, or the backend recomputed the terms since this row was
  // opened). Instead of surfacing that as a scary system error, silently
  // re-hydrate loan terms from the server and tell the user their view was
  // refreshed so they can re-apply the change. Non-409 failures fall through to
  // the standard error toast + the caller's own onFailFn.
  const makeStaleAwareFail = (dealId, onFailFn) => (err) => {
    if (err?.response?.status === 409) {
      enqueueSnackbar(
        "This item changed since you opened it — we refreshed the latest loan terms. Please review and re-apply your change.",
        {
          autoHideDuration: 5000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          content: (key, message) => (
            <CustomSnackbar
              id={key}
              message={message}
              variant="warning"
              closeSnackbar={() => closeSnackbar(key)}
            />
          ),
        },
      );
      // Re-fetch in place so the tab shows the server's current values.
      if (dealId) {
        getLoanTerms({
          dealId,
          onSuccessFn: (data) => dispatch(loanFetchedDataLoanTermsSet(data)),
        });
      }
    }
    if (onFailFn) onFailFn(err);
  };

  // -------------------------------------
  //  ###############TEMPORARY###############
  //  hideFailMsg: true,
  // -------------------------------------
  const getLoanTerms = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/get_loan_terms/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Loan Terms",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      hideFailMsg: true,
      onFailFn,
    });
  };

  const syncLoanWithSalesforceById = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/salesforce_sync/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Sync With Salesforce By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      // The endpoint only publishes to the deal_terms Service Bus topic and
      // returns 202 { queued: true }; the actual Salesforce write runs later in
      // the external subscriber. So this is "queued", not "synced".
      onSuccessMsg: "Salesforce sync queued",
      onCompleteFn,
      onFailFn,
    });
  };

  const getFundingEntities = async ({
    loanId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/get_funding_entities`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Funding Entities",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanTermsListObject = async ({
    dealId,
    objectName,
    data,
    onSuccessFn,
    onSuccessMsg,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectName, data });
    const endpoint = `${ROUTE_PREFIX}/create_list_object/${objectName}/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Loan Terms Object: ${objectName}`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanTermsListObject = async ({
    dealId,
    objectName,
    data,
    itemId,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectName, data, itemId });
    const endpoint = `${ROUTE_PREFIX}/update_list_object/${objectName}/${dealId}/${itemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Terms Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      // Stale-item 409s re-hydrate + friendly toast instead of a system error.
      suppressFailStatuses: [409],
      onFailFn: makeStaleAwareFail(dealId, onFailFn),
    });
  };

  const deleteLoanTermsListObject = async ({
    dealId,
    objectName,
    itemId,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectName, itemId });
    const endpoint = `${ROUTE_PREFIX}/delete_list_object/${objectName}/${dealId}/${itemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Loan Terms Object",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateFundingEntity = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data });
    const endpoint = `${ROUTE_PREFIX}/set_funding_entity/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Set Funding Entity`,
      path: endpoint,
      payload: data,
      onSuccessMsg: "Funding Entity Updated Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateTitleContact = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/set_title_contact/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Set Title Contact`,
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Title Contact Updated Successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  /**
   * ESCROW
   */

  const getEscrowItems = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/get_escrow_items/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Escrow Items",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const setEscrowItem = async ({
    dealId,
    subjectPropertyId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data, subjectPropertyId });
    const endpoint = `${ROUTE_PREFIX}/set_escrow_item/${dealId}/${subjectPropertyId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Set Escrow Item`,
      path: endpoint,
      payload: data,
      onSuccessMsg: "Escrow Item Set Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateEscrowItem = async ({
    dealId,
    escrowItemId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, data, escrowItemId });
    const endpoint = `${ROUTE_PREFIX}/update_escrow_item/${dealId}/${escrowItemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Update Escrow Item`,
      path: endpoint,
      payload: data,
      onSuccessMsg: "Escrow Item Updated Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      // Stale-item 409s re-hydrate + friendly toast instead of a system error.
      suppressFailStatuses: [409],
      onFailFn: makeStaleAwareFail(dealId, onFailFn),
    });
  };

  const deleteEscrowItem = async ({
    dealId,
    escrowItemId,
    // data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, escrowItemId });
    const endpoint = `${ROUTE_PREFIX}/delete_escrow_item/${dealId}/${escrowItemId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Delete Escrow Item`,
      path: endpoint,
      payload: {},
      onSuccessMsg: "Escrow Item Deleted Successfully",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const sendFundingShieldRequest = async ({
    dealId,
    toRecipients,
    attachmentIds,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, toRecipients, attachmentIds });
    const endpoint = `${ROUTE_PREFIX}/send_funding_shield_request/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Sending Funding Shield Request`,
      path: endpoint,
      payload: {
        toRecipients,
        attachmentIds,
      },
      onSuccessMsg: "Funding Shield Request Sent Successfully",
      onFailMsg: "Failed to Send Funding Shield Request",
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanTermsObject = async ({
    dealId,
    objectType, // wireInstructions || otherFutureUpdateableObject
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Loan Updated Successfully",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectType, data });
    const endpoint = `${ROUTE_PREFIX}/create_object/${objectType}/${dealId}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan Terms Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanTermsObject = async ({
    dealId,
    objectType, // wireInstructions || otherFutureUpdateableObject
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Loan Updated Successfully",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, objectType, data });
    const endpoint = `${ROUTE_PREFIX}/update_object/${objectType}/${dealId}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Terms Object Data",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const getEmailTemplateNames = async ({ onSuccessFn, onFailFn }) => {
    const endpoint = `${ROUTE_PREFIX}/email_template_names`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Email Template Names",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const previewEmailTemplate = async ({
    dealId,
    templateName, // wireInstructions || otherFutureUpdateableObject
    onSuccessFn,
    onCompleteFn,

    onFailFn,
  }) => {
    validateRequiredFields({ dealId, templateName });
    const endpoint = `${ROUTE_PREFIX}/preview_email_template/${dealId}/${templateName}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Preview Email Template",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,

      onCompleteFn,
      onFailFn,
    });
  };

  const sendEmailTemplate = async ({
    dealId,
    templateName, // wireInstructions || otherFutureUpdateableObject
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Email Template Sent Successfully",
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, templateName, data });
    const endpoint = `${ROUTE_PREFIX}/send_email_template/${dealId}/${templateName}`; // example endpoint name
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Send Email Template",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    error,
    loading,
    getLoanTerms,
    syncLoanWithSalesforceById,
    getFundingEntities,
    createLoanTermsListObject,
    updateLoanTermsListObject,
    updateTitleContact,
    updateFundingEntity,
    getEscrowItems,
    updateEscrowItem,
    setEscrowItem,
    deleteEscrowItem,
    deleteLoanTermsListObject,
    sendFundingShieldRequest,
    createLoanTermsObject,
    updateLoanTermsObject,
    getEmailTemplateNames,
    sendEmailTemplate,
    previewEmailTemplate,
  };
};
